/**
 * Drawn in code, never generated (style guide v2 §14): gate marks and
 * architectural linework whose geometry is exact.
 */

/** The states of a one-way gate (style guide v2 §10); its words are the step's, beside the mark (core `stepStanding`). */
type GateState = 'closed' | 'next' | 'passed' | 'override' | 'upcoming';

/** One-way gates in drafting notation: outline closed, cedar-centred next, solid passed, a bengara notch for an override. */
function GateMark({ state, size = 14 }: { state: GateState; size?: number }) {
  const s = size;
  const h = s / 2;
  const e = 0.75;
  const outline = `M${h} ${e} L${s - e} ${h} L${h} ${s - e} L${e} ${h} Z`;
  const r = s * 0.17;
  const core = `M${h} ${h - r} L${h + r} ${h} L${h} ${h + r} L${h - r} ${h} Z`;
  const solid = state === 'passed' || state === 'override';
  return (
    <svg
      width={s}
      height={s}
      viewBox={`0 0 ${s} ${s}`}
      className="inline-block shrink-0 align-[-0.12em]"
      aria-hidden
    >
      {solid ? (
        <path d={outline} className="fill-moss" />
      ) : (
        <path d={outline} fill="none" strokeWidth={1.2} className={state === 'upcoming' ? 'stroke-rule-strong' : 'stroke-ink'} />
      )}
      {state === 'next' ? <path d={core} className="fill-cedar" /> : null}
      {state === 'override' ? <path d={`M${s - e} ${h} L${s - e - s * 0.3} ${h - s * 0.3} L${s - e - s * 0.3} ${h + s * 0.3} Z`} className="fill-alert" /> : null}
    </svg>
  );
}

/**
 * A Japanese timber frame in gable-end elevation, as NSB draws one: posts on
 * stones, two nuki through them, a cambered log tie beam, koya-zuka struts
 * carrying the purlins and ridge, and rafters out to deep eaves that lift at
 * the tips. Exact linework, never generated (guide §20): the sign-in art is
 * painted land, and this is drawn over it.
 */
export function FrameElevation({ className = '' }: { className?: string }) {
  // Units: 1 ft = 10 px. Two 10 ft bays, a 5.5-in-10 roof, eaves 6 ft past the posts.
  const ground = 300;
  const posts = [140, 240, 340];
  const pitch = 0.55;
  const [beamL, beamR] = [118, 362];
  // The tie beam is a log, cambered: its top and underside as y at x.
  const camber = (x: number, end: number, crown: number) => {
    const t = (x - beamL) / (beamR - beamL);
    return end + 2 * t * (1 - t) * (crown - end);
  };
  const beamTop = (x: number) => camber(x, 190, 176);
  const beamUnder = (x: number) => camber(x, 200, 186);
  const plate = beamTop(140) - 10; // top of the eave beams over the outer posts, where the rafters bear
  const roofAt = (x: number) => plate - (100 - Math.abs(x - 240)) * pitch;
  const ridge = roofAt(240);
  const out = 60 * pitch; // the straight run to the eave tip
  const tip = plate + out - 6; // lifted at the tip
  const f = (n: number) => n.toFixed(1);
  const rafters = (dy: number) =>
    `M80 ${f(tip + dy)} Q110 ${f(plate + out / 2 + dy)} 140 ${f(plate + dy)} L240 ${f(ridge + dy)} ` +
    `L340 ${f(plate + dy)} Q370 ${f(plate + out / 2 + dy)} 400 ${f(tip + dy)}`;
  return (
    <svg viewBox="0 0 480 380" className={className} aria-hidden="true" fill="none">
      <g className="stroke-ink" strokeWidth={1} vectorEffect="non-scaling-stroke" opacity={0.55}>
        <path d={`M40 ${ground} H440`} />
        {posts.map((x) => (
          <path
            key={x}
            d={
              // the stone, then the post standing on it
              `M${x - 12} ${ground} Q${x - 12} ${ground - 8} ${x - 5} ${ground - 8} H${x + 5} Q${x + 12} ${ground - 8} ${x + 12} ${ground} ` +
              `M${x} ${ground - 8} V${f(beamUnder(x))}`
            }
          />
        ))}
        {/* two nuki through the posts */}
        <path d="M128 262 H352 M128 230 H352" />
        {/* the cambered tie beam */}
        <path d={`M${beamL} 190 Q240 176 ${beamR} 190 M${beamL} 200 Q240 186 ${beamR} 200 M${beamL} 190 V200 M${beamR} 190 V200`} />
        {/* eave beams over the outer posts */}
        {[140, 340].map((x) => (
          <path key={x} d={`M${x - 5} ${f(plate + 10)} V${f(plate)} H${x + 5} V${f(plate + 10)}`} />
        ))}
        {/* koya-zuka struts, each carrying a purlin; the middle one the ridge */}
        {[173, 207, 240, 273, 307].map((x) => {
          const top = roofAt(x);
          return <path key={x} d={`M${x} ${f(beamTop(x))} V${f(top + 8)} M${x - 4} ${f(top)} V${f(top + 8)} H${x + 4} V${f(top)}`} />;
        })}
        {/* rafters and roof, the eaves lifting at the tips, and the ridge cap */}
        <path d={rafters(0)} />
        <path d={rafters(-10)} />
        <path d={`M80 ${f(tip)} V${f(tip - 10)} M400 ${f(tip)} V${f(tip - 10)}`} />
        <path d={`M228 ${f(ridge - 10)} V${f(ridge - 15)} H252 V${f(ridge - 10)}`} />
      </g>
      {/* dimension line: the span */}
      <g className="stroke-ink-3" strokeWidth={1} opacity={0.8}>
        <path d={`M140 ${ground + 14} V${ground + 40} M340 ${ground + 14} V${ground + 40} M134 ${ground + 32} H346`} />
        <path d={`M136 ${ground + 36} L144 ${ground + 28}`} className="stroke-cedar" strokeWidth={1.4} />
        <path d={`M336 ${ground + 36} L344 ${ground + 28}`} className="stroke-cedar" strokeWidth={1.4} />
      </g>
      {/* rise mark, ground to ridge */}
      <g className="stroke-ink-3" strokeWidth={1} opacity={0.6}>
        <path d={`M430 ${ground} V${f(ridge - 15)}`} strokeDasharray="2 3" />
      </g>
    </svg>
  );
}

export type StepMarkKind = 'work' | 'group' | 'milestone' | 'decision' | 'inspection' | 'gate' | 'start' | 'end';
export type StepMarkState = 'done' | 'now' | 'next' | 'ahead' | 'ghost' | 'override';

/**
 * The marks of a process drawing, systematic like the gate mark (style guide
 * §3, Otl Aicher): a square for work (a square within a square for a step that
 * holds steps), a circle for a landmark, a pennant for the client's decision,
 * the diamond for a gate, and a diamond standing on a line for an inspection:
 * the authority's hold, drawn open where the client's gate is drawn closed. The
 * ends of a process are marked as a drawing of one marks them: a solid dot
 * where it starts, a dot in a ring where it ends.
 * Solid moss when done, half-filled under way, a cedar core for what is next,
 * a dashed outline for a step that could be turned on. Never the only signal:
 * every row also says its state in words.
 */
export function StepMark({ kind, state, size = 14 }: { kind: StepMarkKind; state: StepMarkState; size?: number }) {
  if (kind === 'gate') {
    const g = state === 'done' ? 'passed' : state === 'override' ? 'override' : state === 'next' || state === 'now' ? 'next' : state === 'ghost' ? 'upcoming' : 'closed';
    return <GateMark state={g} size={size} />;
  }
  const s = size;
  const h = s / 2;
  const dashed = state === 'ghost' ? '2 2' : undefined;
  const line = state === 'ghost' ? 'stroke-rule-strong' : 'stroke-ink';
  const solid = state === 'done' || state === 'override';
  const core = state === 'next' || state === 'now';
  const common = { 'aria-hidden': true as const, width: s, height: s, viewBox: `0 0 ${s} ${s}`, className: 'inline-block shrink-0 align-[-0.12em] overflow-visible' };
  if (kind === 'inspection') {
    // A small diamond on its baseline: the inspector's visit, passed or not; a bengara notch for an override.
    const d = `M${h} ${s * 0.08} L${s * 0.86} ${h * 0.86} L${h} ${s * 0.78} L${s * 0.14} ${h * 0.86} Z`;
    return (
      <svg {...common}>
        <path d={d} className={solid ? 'fill-moss' : `${line} fill-paper`} strokeWidth={solid ? 0 : 1.2} strokeLinejoin="round" strokeDasharray={dashed} />
        <path d={`M${s * 0.1} ${s * 0.92} H${s * 0.9}`} className={state === 'ghost' ? 'stroke-rule-strong' : 'stroke-ink'} strokeWidth={1.2} strokeDasharray={dashed} />
        {core ? <circle cx={h} cy={h * 0.86} r={s * 0.13} className="fill-cedar" /> : null}
        {state === 'override' ? <circle cx={s * 0.86} cy={s * 0.2} r={s * 0.14} className="fill-alert" /> : null}
      </svg>
    );
  }
  if (kind === 'start' || kind === 'end') {
    const r = h - 1;
    const dot = state === 'ghost' ? 'fill-rule-strong' : 'fill-ink';
    return (
      <svg {...common}>
        {kind === 'end' ? <circle cx={h} cy={h} r={r} className={`${line} fill-paper`} strokeWidth={1.2} strokeDasharray={dashed} /> : null}
        <circle cx={h} cy={h} r={kind === 'end' ? r * 0.5 : r * 0.8} className={dot} />
      </svg>
    );
  }
  if (kind === 'milestone') {
    const r = h - 1;
    return (
      <svg {...common}>
        <circle cx={h} cy={h} r={r} className={solid ? 'fill-moss' : `${line} fill-paper`} strokeWidth={solid ? 0 : 1.2} strokeDasharray={dashed} />
        {core ? <circle cx={h} cy={h} r={s * 0.17} className="fill-cedar" /> : null}
      </svg>
    );
  }
  if (kind === 'decision') {
    const p = `M${s * 0.18} ${s * 0.12} L${s * 0.9} ${h} L${s * 0.18} ${s * 0.88} Z`;
    return (
      <svg {...common}>
        <path d={p} className={solid ? 'fill-moss' : `${line} fill-paper`} strokeWidth={solid ? 0 : 1.2} strokeLinejoin="round" strokeDasharray={dashed} />
        {core ? <circle cx={s * 0.42} cy={h} r={s * 0.14} className="fill-cedar" /> : null}
      </svg>
    );
  }
  const e = 1.5;
  const box = { x: e, y: e, width: s - 2 * e, height: s - 2 * e };
  return (
    <svg {...common}>
      <rect {...box} className={solid ? 'fill-moss' : `${line} fill-paper`} strokeWidth={solid ? 0 : 1.2} strokeDasharray={dashed} />
      {state === 'now' ? <rect x={e} y={e} width={(s - 2 * e) / 2} height={s - 2 * e} className="fill-ink" /> : null}
      {state === 'next' ? <rect x={h - s * 0.15} y={h - s * 0.15} width={s * 0.3} height={s * 0.3} className="fill-cedar" /> : null}
      {kind === 'group' ? <rect x={s * 0.32} y={s * 0.32} width={s * 0.36} height={s * 0.36} fill="none" strokeWidth={1} className={solid ? 'stroke-paper' : line} /> : null}
    </svg>
  );
}

/**
 * The pause mark (L-25b: a paused project's tag "with the pause icon"): two upright bars, exact, drawn in the text's own
 * colour before the tag's word, hidden from a screen reader so the word names it.
 */
export function PauseMark() {
  return (
    <svg aria-hidden="true" viewBox="0 0 8 10" width="0.54em" height="0.7em" className="mr-[0.4em] inline-block align-baseline" fill="currentColor">
      <rect x="0.5" y="0" width="2.5" height="10" />
      <rect x="5" y="0" width="2.5" height="10" />
    </svg>
  );
}

/** Lines as the small drawings draw them: ink or a colour, round-ended. */
const DRAW = { fill: 'none', strokeWidth: 1.25, strokeLinecap: 'round', strokeLinejoin: 'round' } as const;

/** The arrow from a word to the button it points at: a short curve down and to the right. */
export function PointerArrow({ className = '' }: { className?: string }) {
  return (
    <svg viewBox="0 0 40 30" width={40} height={30} className={`block ${className}`} aria-hidden="true">
      <path d="M4 3c6 2 14 8 22 20" className="stroke-alert" {...DRAW} strokeWidth={1.5} />
      <path d="M19 20l7 3 1-7" className="stroke-alert" {...DRAW} strokeWidth={1.5} />
    </svg>
  );
}
